import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const NAVY = [18, 58, 92];
const MAROON = [109, 31, 44];

const typeLabel = (t) => (t === "registration" ? "Registration" : "Admission");

// Detailed single-application PDF with college header/logo, all fields, photo & signature.
export function downloadApplicationPdf(app, college = {}, logo = "") {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  if (logo) { try { doc.addImage(logo, "PNG", 40, 26, 56, 56); } catch (e) { /* ignore */ } }
  doc.setFontSize(18); doc.setFont("times", "bold");
  doc.text(college.name || "Kanggui Christian College", W / 2, 50, { align: "center" });
  doc.setFontSize(10); doc.setFont("times", "normal");
  doc.text(`${college.affiliation || ""}${college.affNo ? ` (Aff. No. ${college.affNo})` : ""}`, W / 2, 68, { align: "center" });
  doc.text(college.location || "", W / 2, 82, { align: "center" });
  doc.setDrawColor(...NAVY); doc.setLineWidth(1.2); doc.line(40, 92, W - 40, 92);
  doc.setFontSize(13); doc.setFont("times", "bold");
  doc.text(`${typeLabel(app.form_type)} Application Form`, W / 2, 114, { align: "center" });
  if (app.photo) { try { doc.addImage(app.photo, "JPEG", W - 130, 130, 90, 108); } catch (e) { /* ignore */ } }
  const rows = [
    ["Name", app.name], ["Father's Name", app.father_name], ["Mother's Name", app.mother_name],
    ["Date of Birth", app.dob],
    app.form_type === "registration" ? ["Semester", app.semester] : ["Course Applied", app.course],
    ["Department", app.department],
    ["Roll No.", app.roll_no], ["ABC ID", app.abc_id], ["Aadhar No.", app.aadhar_no],
    ["Registration No.", app.registration_no],
    ["Email", app.email],
    ["Primary Phone", app.phone], ["Secondary Phone", app.phone_secondary],
    ["Present Address", app.present_address], ["Permanent Address", app.permanent_address],
  ].filter((r) => r[1]);
  autoTable(doc, {
    startY: 250, head: [["Field", "Details"]], body: rows,
    styles: { fontSize: 10, cellPadding: 5 }, headStyles: { fillColor: NAVY },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 160 } },
  });
  if (app.academic && app.academic.length) {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 14,
      head: [["Institution", "Exam/University", "Marks", "Division", "Year"]],
      body: app.academic.map((a) => [a.institution, a.university_exam, a.total_marks, a.division, a.year]),
      styles: { fontSize: 9, cellPadding: 4 }, headStyles: { fillColor: MAROON },
    });
  }
  const y = (doc.lastAutoTable?.finalY || 700) + 40;
  if (app.signature) { try { doc.addImage(app.signature, "JPEG", 40, y, 100, 40); } catch (e) { /* ignore */ } }
  doc.setFontSize(9); doc.setFont("times", "normal");
  doc.text("Applicant Signature", 40, y + 52);
  doc.text(`Submitted: ${(app.created_at || "").slice(0, 10)}`, W - 40, y + 52, { align: "right" });
  doc.save(`${app.form_type}_${(app.name || "application").replace(/\s+/g, "_")}.pdf`);
}

const listColumns = (formType) => [
  ["#", (a, i) => i + 1],
  ["Name", (a) => a.name || ""],
  ["Father's Name", (a) => a.father_name || ""],
  ["Mother's Name", (a) => a.mother_name || ""],
  ["DOB", (a) => a.dob || ""],
  formType === "registration" ? ["Semester", (a) => a.semester || ""] : ["Course", (a) => a.course || ""],
  ["Department", (a) => a.department || ""],
  ["Roll No.", (a) => a.roll_no || ""],
  ["ABC ID", (a) => a.abc_id || ""],
  ["Aadhar No.", (a) => a.aadhar_no || ""],
  ["Reg. No.", (a) => a.registration_no || ""],
  ["Email", (a) => a.email || ""],
  ["Phone", (a) => a.phone || ""],
  ["Phone 2", (a) => a.phone_secondary || ""],
  ["Present Address", (a) => a.present_address || ""],
  ["Permanent Address", (a) => a.permanent_address || ""],
  ["Submitted", (a) => (a.created_at || "").slice(0, 10)],
];

// Full list of applications as a landscape PDF table.
export function downloadApplicationsListPdf(apps, formType, college = {}, filterLabel = "") {
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
  const W = doc.internal.pageSize.getWidth();
  doc.setFontSize(15); doc.setFont("times", "bold");
  doc.text(college.name || "Kanggui Christian College", W / 2, 40, { align: "center" });
  doc.setFontSize(11); doc.setFont("times", "normal");
  doc.text(`${typeLabel(formType)} Applications${filterLabel ? ` — ${filterLabel}` : ""} — ${apps.length} record(s)`, W / 2, 58, { align: "center" });
  const cols = listColumns(formType);
  autoTable(doc, {
    startY: 74,
    head: [cols.map((c) => c[0])],
    body: apps.map((a, i) => cols.map((c) => String(c[1](a, i)))),
    styles: { fontSize: 7, cellPadding: 3, overflow: "linebreak" },
    headStyles: { fillColor: NAVY, fontSize: 7 },
    columnStyles: { 0: { cellWidth: 22 } },
  });
  const slug = filterLabel ? `_${filterLabel.replace(/\s+/g, "_")}` : "";
  doc.save(`${formType}${slug}_applications_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// Full list of applications as an Excel (.xlsx) workbook (photos/signatures excluded).
export function downloadApplicationsExcel(apps, formType, filterLabel = "") {
  const cols = listColumns(formType);
  const aoa = [cols.map((c) => c[0])];
  apps.forEach((a, i) => aoa.push(cols.map((c) => c[1](a, i))));
  // Academic history flattened into a single readable cell per applicant.
  aoa[0].push("Academic History");
  apps.forEach((a, idx) => {
    const acad = (a.academic || [])
      .map((r) => `${r.institution || ""} | ${r.university_exam || ""} | ${r.total_marks || ""} | ${r.division || ""} | ${r.year || ""}`)
      .join(" ; ");
    aoa[idx + 1].push(acad);
  });
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, typeLabel(formType).slice(0, 28));
  const slug = filterLabel ? `_${filterLabel.replace(/\s+/g, "_")}` : "";
  XLSX.writeFile(wb, `${formType}${slug}_applications_${new Date().toISOString().slice(0, 10)}.xlsx`);
}
